import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, ArrowRight, TrendingUp, Trophy } from "lucide-react";
import api from "../lib/api";
import { fmt } from "../lib/money";

/**
 * Compact homepage CTA shown to signed-in users that surfaces:
 *   • month-to-date affiliate earnings (delivered) + pending
 *   • how many orders earned them this month
 *   • the platform's highest-G$-per-sale product as a "share this next" pick
 *
 * Stays fully hidden if the user is signed-out or the API call fails.
 */
export default function AffiliateMonthWidget() {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get("/affiliate/month-summary")
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { /* signed-out / 401 — render nothing */ });
    return () => { alive = false; };
  }, []);

  if (!data) return null;
  const { earned_mtd, pending_mtd, orders_mtd, unpaid_balance, top_product, hot_today } = data;
  const monthName = new Date().toLocaleString("en", { month: "long" });

  return (
    <section
      data-testid="affiliate-month-widget"
      className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-10 sm:pt-14"
    >
      <div className="border-2 border-black bg-[#FFE600] relative overflow-hidden">
        {/* Decorative stripe */}
        <div className="absolute -right-12 top-0 bottom-0 w-32 bg-black/5 -skew-x-12 pointer-events-none" />

        <div className="grid lg:grid-cols-[1.4fr_1fr] divide-y lg:divide-y-0 lg:divide-x-2 divide-black/10 relative">
          {/* LEFT — earnings stats */}
          <div className="p-5 sm:p-7">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold mb-3">
              <Wallet className="w-3.5 h-3.5" />
              Your {monthName} affiliate earnings
            </div>
            <div className="flex items-baseline gap-3 flex-wrap">
              <span
                data-testid="aff-month-earned"
                className="font-display font-black text-4xl sm:text-6xl leading-none"
              >
                {fmt(earned_mtd)}
              </span>
              <span className="text-[11px] uppercase tracking-widest font-bold text-black/70">
                earned this month
              </span>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[12px] sm:text-sm">
              <span data-testid="aff-month-orders">
                <b>{orders_mtd}</b> {orders_mtd === 1 ? "order" : "orders"} this month
              </span>
              {pending_mtd > 0 && (
                <span className="text-black/70">
                  <b>{fmt(pending_mtd)}</b> pending delivery
                </span>
              )}
              {unpaid_balance > 0 && (
                <span data-testid="aff-month-unpaid" className="text-[#0033FF] font-bold">
                  {fmt(unpaid_balance)} ready to request →
                </span>
              )}
            </div>
            <Link
              to="/affiliate"
              data-testid="aff-month-dashboard-link"
              className="mt-4 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold underline underline-offset-4 hover:bg-black hover:text-[#FFE600] px-1 py-0.5 transition-colors"
            >
              See your affiliate dashboard <ArrowRight className="w-3 h-3" />
            </Link>
            <Link
              to="/leaderboard"
              data-testid="aff-month-leaderboard-link"
              className="mt-4 ml-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-bold underline underline-offset-4 hover:bg-black hover:text-[#FFE600] px-1 py-0.5 transition-colors"
            >
              <Trophy className="w-3 h-3" /> Leaderboard
            </Link>
          </div>

          {/* RIGHT — top product pitch */}
          {top_product && (
            <Link
              to={`/products/${top_product.product_id}`}
              data-testid="aff-month-top-product"
              className="p-5 sm:p-7 flex items-center gap-4 group bg-black text-[#FFE600] hover:bg-[#0033FF] hover:text-white transition-colors"
            >
              <div className="aspect-square w-20 sm:w-24 shrink-0 bg-white/10 overflow-hidden border border-current/20">
                {top_product.image ? (
                  <img
                    src={top_product.image.startsWith("http") ? top_product.image : top_product.image}
                    alt={top_product.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full grid place-items-center">
                    <TrendingUp className="w-8 h-8 opacity-50" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[9px] uppercase tracking-[0.2em] opacity-70 mb-1">
                  Share this next · highest payout
                </div>
                <div className="font-display font-black text-base sm:text-lg leading-tight line-clamp-2">
                  {top_product.title}
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className="font-display font-black text-xl sm:text-2xl">
                    +{fmt(top_product.earn_per_sale)}
                  </span>
                  <span className="text-[10px] uppercase tracking-widest opacity-80">
                    per sale · {top_product.commission_pct}%
                  </span>
                </div>
              </div>
              <ArrowRight className="w-5 h-5 shrink-0 group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
        </div>
      </div>

      {/* "Hot today" mini-row of more share-worthy products */}
      {hot_today && hot_today.length > 0 && (
        <div className="mt-4 border-2 border-black bg-white" data-testid="aff-hot-today">
          <div className="flex items-center justify-between px-4 sm:px-5 py-2 border-b-2 border-black bg-[#FF2A00] text-white">
            <span className="font-display font-black text-[11px] sm:text-xs uppercase tracking-[0.2em] flex items-center gap-1.5">
              <TrendingUp className="w-3.5 h-3.5" /> Hot today · 3 more to share
            </span>
            <span className="text-[10px] uppercase tracking-widest font-bold opacity-80 hidden sm:inline">
              Click a card → product page → share button
            </span>
          </div>
          <div className="grid grid-cols-3 divide-x-2 divide-black">
            {hot_today.map((p, i) => (
              <Link
                key={p.product_id}
                to={`/products/${p.product_id}`}
                data-testid={`aff-hot-product-${i}`}
                className="p-3 sm:p-4 flex items-center gap-2 sm:gap-3 group hover:bg-[#FFE600] transition-colors min-w-0"
              >
                <div className="aspect-square w-12 sm:w-16 shrink-0 bg-neutral-100 overflow-hidden border border-neutral-200">
                  {p.image ? (
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full grid place-items-center text-neutral-300">
                      <TrendingUp className="w-5 h-5" />
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-[12px] sm:text-sm leading-tight line-clamp-2 group-hover:text-black">
                    {p.title}
                  </div>
                  <div className="mt-0.5 flex items-baseline gap-1 flex-wrap">
                    <span className="font-display font-black text-base sm:text-lg text-[#FF2A00] group-hover:text-black">
                      +{fmt(p.earn_per_sale)}
                    </span>
                    <span className="text-[9px] uppercase tracking-widest text-neutral-500 group-hover:text-black/70">
                      / sale · {p.commission_pct}%
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
